import { FACET_HANDLER_SYMBOL, FACET_REMOVE_HANDLER_SYMBOL, MESSAGE_HANDLER_SYMBOL, MESSAGE_OR_MESSAGE_REQUEST_HANDLER_SYMBOL, MESSAGE_REQUEST_HANDLER_SYMBOL, MODEL_EVENT_HANDLER_SYMBOL, ModelEventType } from "./messageHandlerDecorators";
import { modelPool } from "@modules/model/ModelPool";
/**
 * Base class for classes that can register message/facet handlers via decorators
 */
export class MessagingBase {
    constructor(connection) {
        this.connection = connection;
        // Event handlers
        this.messageHandlers = new Map();
        this.messageRequestHandlers = new Map();
        this.messageOrMessageRequestHandlers = new Map();
        this.facetHandlers = new Map();
        this.facetRemoveHandlers = new Map();
        this.modelEventHandlers = new Map();
        // Register event handlers defined by decorators
        const proto = Object.getPrototypeOf(this);
        this.registerDecoratorMessageHandlers(proto);
        this.registerDecoratorMessageRequestHandlers(proto);
        this.registerDecoratorMessageOrMessageRequestHandlers(proto);
        this.registerDecoratorFacetHandlers(proto);
        this.registerDecoratorFacetRemoveHandlers(proto);
        this.registerDecoratorModelEventHandlers(proto);
    }
    /// Private decorator helper methods ///
    registerDecoratorMessageHandlers(proto) {
        var _a;
        const entries = (_a = Reflect.get(proto, MESSAGE_HANDLER_SYMBOL)) !== null && _a !== void 0 ? _a : [];
        for (const [messageTypes, messageTypeHandler] of entries) {
            const busMessageTypes = Array.isArray(messageTypes)
                ? messageTypes
                : [messageTypes];
            const handler = this[messageTypeHandler];
            for (const messageType of busMessageTypes) {
                const messageTypeString = messageType.typestatic;
                if (this.messageHandlers.has(messageTypeString)) {
                    console.error(`[module] A message handler was already provided for ${messageTypeString}`);
                    continue; // We don't abort all registration
                }
                this.messageHandlers.set(messageTypeString, handler);
                this.connection.subscribeByType(messageType, handler.bind(this));
            }
        }
    }
    registerDecoratorMessageRequestHandlers(proto) {
        var _a;
        const entries = (_a = Reflect.get(proto, MESSAGE_REQUEST_HANDLER_SYMBOL)) !== null && _a !== void 0 ? _a : [];
        for (const [messageTypes, messageTypeHandler] of entries) {
            const busMessageTypes = Array.isArray(messageTypes)
                ? messageTypes
                : [messageTypes];
            const handler = this[messageTypeHandler];
            for (const messageType of busMessageTypes) {
                const messageTypeString = messageType.typestatic;
                if (this.messageRequestHandlers.has(messageTypeString)) {
                    console.error(`[module] A message handler was already provided for ${messageTypeString}`);
                    continue; // We don't abort all registration
                }
                this.messageRequestHandlers.set(messageTypeString, handler);
                this.connection.subscribeForAskRequest(messageType, handler.bind(this));
            }
        }
    }
    registerDecoratorMessageOrMessageRequestHandlers(proto) {
        var _a;
        const entries = (_a = Reflect.get(proto, MESSAGE_OR_MESSAGE_REQUEST_HANDLER_SYMBOL)) !== null && _a !== void 0 ? _a : [];
        for (const [messageTypes, messageTypeHandler] of entries) {
            const busMessageTypes = Array.isArray(messageTypes)
                ? messageTypes
                : [messageTypes];
            const handler = this[messageTypeHandler];
            for (const messageType of busMessageTypes) {
                const messageTypeString = messageType.typestatic;
                if (this.messageOrMessageRequestHandlers.has(messageTypeString)) {
                    console.error(`[module] A message handler was already provided for ${messageTypeString}`);
                    continue; // We don't abort all registration
                }
                this.messageOrMessageRequestHandlers.set(messageTypeString, handler);
                this.connection.subscribeByTypeOrAskRequest(messageType, handler.bind(this));
            }
        }
    }
    registerDecoratorFacetHandlers(proto) {
        var _a;
        const entries = (_a = Reflect.get(proto, FACET_HANDLER_SYMBOL)) !== null && _a !== void 0 ? _a : [];
        for (const [facetType, facetTypeHandler] of entries) {
            const typeKey = facetType.typestatic;
            const handler = this[facetTypeHandler];
            if (this.facetHandlers.has(typeKey)) {
                console.error(`[module] A facet handler was already provided for ${typeKey}`);
                continue; // We don't abort all registration
            }
            this.facetHandlers.set(typeKey, handler);
            this.connection.subscribeByFacetType(facetType, handler.bind(this));
        }
    }
    registerDecoratorFacetRemoveHandlers(proto) {
        var _a;
        const entries = (_a = Reflect.get(proto, FACET_REMOVE_HANDLER_SYMBOL)) !== null && _a !== void 0 ? _a : [];
        for (const [facetType, facetTypeHandler] of entries) {
            const typeKey = facetType.typestatic;
            const handler = this[facetTypeHandler];
            if (this.facetRemoveHandlers.has(typeKey)) {
                console.error(`[module] A facet remove handler was already provided for ${typeKey}`);
                continue; // We don't abort all registration
            }
            this.facetRemoveHandlers.set(typeKey, handler);
            this.connection.subscribeByRemoveFacetType(facetType, handler.bind(this));
        }
    }
    registerDecoratorModelEventHandlers(proto) {
        var _a;
        const entries = (_a = Reflect.get(proto, MODEL_EVENT_HANDLER_SYMBOL)) !== null && _a !== void 0 ? _a : [];
        for (const [modelEventType, modelEventHandlers] of entries) {
            if (this.modelEventHandlers.has(modelEventType)) {
                console.error(`[module] A model event handler was already provided for ${modelEventType}`);
                continue; // We don't abort all registration
            }
            const handler = this[modelEventHandlers];
            let unsubscribe;
            switch (modelEventType) {
                case ModelEventType.MODEL_CREATED:
                    unsubscribe = modelPool.setOnModelCreatedHandler(handler.bind(this));
                    break;
                case ModelEventType.MODEL_DESTROYED:
                    unsubscribe = modelPool.setOnModelDestroyedHandler(handler.bind(this));
                    break;
                case ModelEventType.FACET_UPDATED:
                    unsubscribe = modelPool.setOnModelFacetChangedHandler(handler.bind(this));
                    break;
                case ModelEventType.FACET_REMOVED:
                    unsubscribe = modelPool.setOnModelFacetRemovedHandler(handler.bind(this));
                    break;
                case ModelEventType.TRANSFORM_UPDATED:
                    unsubscribe = modelPool.setOnModelTransformChangedHandler(handler.bind(this));
                    break;
                case ModelEventType.ROOM_CHANGED:
                    unsubscribe = modelPool.setOnModelRoomChangedHandler(handler.bind(this));
                    break;
                case ModelEventType.PARENT_CHANGED:
                    unsubscribe = modelPool.setOnModelParentChangedHandler(handler.bind(this));
                    break;
                default:
                    throw new Error(`Unrecognized model event type ${ModelEventType[modelEventType]}`);
            }
            this.modelEventHandlers.set(modelEventType, unsubscribe);
        }
    }
}
